import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { ToastService } from '../../../../shared/toast/toast.service';
import { PopiManagerService, SchemaSummaryResult, SaveEntityMappingRequest, EntityFieldMapping  } from '../../../../../service/proxy';

interface SaveMappingRequestDto {
    displayName: string;
    dbName: string;
    tableName: string;
    parentTableName?: string;
    parentIdColumn?: string;
    identifierColumn?: string;
    fields: EntityFieldMapping[];
}

@Component({
    templateUrl: './create-or-update-mapping.component.html',
    styleUrls: ['./create-or-update-mapping.component.scss'],
    standalone: true,

    imports: [
        CommonModule,
        FormsModule,
    ],

    providers:[
        ToastService,
        PopiManagerService
    ]
})
export class CreateOrUpdateMappingComponent implements OnInit {
    public formValues = {
        displayName: '',
        dbName: '',
        tableName: '',
        parentTableName: '',
        parentIdColumn: '',
        identifierColumn: '',
    };

    public schemaSummaries: SchemaSummaryResult = new SchemaSummaryResult();

    public tables: any[] = [];
    public fields: any[] = [];
    public schemaFields: any[] = [];
    public parentFields: any[] = [];

    public selectedDbName = '';
    public selectedTableName = '';
    public isSaving = false;

    public newField = {
        fieldName: '',
        displayName: '',
        replaceValue: '',
        search: false,
        piField: false,
    };

    public model: SaveEntityMappingRequest = new SaveEntityMappingRequest();

    constructor(
        private popiService: PopiManagerService,
        private toastService: ToastService,
        private router: Router,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        const entityId = this.route.snapshot.queryParamMap.get('id');

        this.popiService.getSchemaSummaries()
            .subscribe({
                next: (data) => {
                    this.schemaSummaries = data || new SchemaSummaryResult();

                    if (entityId && entityId != '0') {
                        this.loadEntityMapping(+entityId);
                    }
                },
                error: () => {
                    this.toastService.show('Failed to load schema summaries.', 'error');
                }
            }
        );
    }

    loadSchemaSummaries() {
        this.popiService.getSchemaSummaries()
            .subscribe({
                next: (data) => {
                    this.schemaSummaries = data || new SchemaSummaryResult();
                },
                error: () => {
                    this.toastService.show('Failed to load schema summaries.', 'error');
                }
            }
        );
    }

    onDbChange() {
        this.selectedDbName = this.formValues.dbName;
        this.tables = [];

        if (!this.schemaSummaries?.summaries?.length) {
            return;
        }

        const db = this.schemaSummaries.summaries.find(x => x.dbName === this.selectedDbName);
        this.tables = db?.tables || [];

        this.schemaFields = [];
        this.parentFields = [];

        this.formValues.tableName = '';
        this.formValues.parentTableName = '';
        this.formValues.parentIdColumn = '';
        this.formValues.identifierColumn = '';
    }

    onTableChange() {
        this.selectedTableName = this.formValues.tableName;

        const db = this.schemaSummaries.summaries.find(x => x.dbName === this.selectedDbName);
        const table = db?.tables.find(t => t.tableName === this.selectedTableName);

        if (table) {
            this.schemaFields = table.fields.map(field => ({
                fieldName: field.name,
                displayName: field.name,
                replaceValue: '',
                search: false,
                piField: false,
            }));
        }
    }

    onParentTableChange() {
        const tableName = this.formValues.parentTableName;

        const db = this.schemaSummaries.summaries.find(x => x.dbName === this.selectedDbName);
        const table = db?.tables.find(t => t.tableName === tableName);

        if (table) {
            this.parentFields = table.fields.map(field => ({
                fieldName: field.name,
                displayName: field.name,
                replaceValue: '',
                search: false,
                piField: false,
            }));
        }
    }

    saveMapping() {
        if (this.isSaving) {
            return;
        }

        if (!this.formValues.displayName || !this.formValues.dbName || !this.formValues.tableName) {
            this.toastService.show('Please complete all required fields.', 'warning');
            return;
        }

        if (this.formValues.parentTableName && (!this.formValues.parentIdColumn || !this.formValues.identifierColumn)) {
            this.toastService.show('Please complete the mapping for: '+this.formValues.parentTableName, 'warning');
            return;
        }

        if (!this.fields.length) {
            this.toastService.show('Please add at least one POPI field.', 'warning');
            return;
        }

        for (const field of this.fields) {
            if (!this.validateNewField(field)) {
                this.toastService.show('One or more fields are invalid.', 'warning');
                return;
            }
        }

        this.isSaving = true;

        this.model.displayName = this.formValues.displayName;
        this.model.dbName = this.formValues.dbName;
        this.model.tableName = this.formValues.tableName;
        this.model.parentTableName = this.formValues.parentTableName;
        this.model.parentIdColumn = this.formValues.parentIdColumn;
        this.model.identifierColumn = this.formValues.identifierColumn;
        this.model.fields = this.fields;

        const payload = new SaveEntityMappingRequest({
            displayName: this.model.displayName,
            dbName: this.model.dbName,
            tableName: this.model.tableName,
            parentTableName: this.model.parentTableName,
            parentIdColumn: this.model.parentIdColumn,
            identifierColumn: this.model.identifierColumn,
            fields: this.model.fields.map(f => new EntityFieldMapping(f))
        });

        this.popiService.saveEntityMapping(payload)
            .subscribe({
                next: (response) => {
                    this.isSaving = false;

                    if (response.success) {
                        this.toastService.show(response.message, 'success');

                        setTimeout(() => {
                            this.router.navigate(['/popi/list']);
                        }, 1500);
                    }
                    else {
                        this.toastService.show(response.message, 'warning');
                    }
                },
                error: (err) => {
                    this.isSaving = false;
                    this.toastService.show('Failed to save mapping: ' + (err.error?.message || 'Unknown error'), 'error');
                }
            }
        );
    }

    saveNewField() {
        if (!this.validateNewField(this.newField)) {
            return;
        }

        this.fields.push({ ...this.newField });

        this.newField = {
            fieldName: '',
            displayName: '',
            replaceValue: '',
            search: false,
            piField: false
        };
    }

    validateNewField(field: any): boolean {
        if (!field.fieldName) {
          this.toastService.show('Field Name is required.', 'warning');
          return false;
        }

        if (!field.displayName) {
            field.displayName = field.fieldName;
        }

        if (field.piField && !field.replaceValue?.trim()) {
          this.toastService.show('Replace Value is required for de-identified fields.', 'warning');
          return false;
        }

        if (field.replaceValue == null) {
            field.replaceValue = '';
        }

        if (field.search == null) {
            field.search = false;
        }

        if (field.piField == null) {
            field.piField = false;
        }

        return true;
    }

    removeField(index: number) {
        this.fields.splice(index, 1);
    }

    loadEntityMapping(id: number) {
        this.popiService.getEntityMappingById(id)
            .subscribe({
                next: (data) => {
                    if (!data) {
                        this.toastService.show('Could not load mapping.', 'error');
                        return;
                    }

                    this.formValues.displayName = data.displayName;
                    this.formValues.dbName = data.dbName;
                    this.formValues.tableName = data.tableName;
                    this.formValues.parentTableName = data.parentTableName;
                    this.formValues.parentIdColumn = data.parentIdColumn;
                    this.formValues.identifierColumn = data.identifierColumn;
                    this.fields = data.fields || [];

                    this.selectedDbName = data.dbName;
                    this.selectedTableName = data.tableName;

                    const db = this.schemaSummaries.summaries.find(x => x.dbName === this.selectedDbName);

                    if (db) {
                        this.tables = db.tables || [];
                        const table = db.tables.find(t => t.tableName === this.selectedTableName);

                        if (table) {
                            this.schemaFields = table.fields.map(field => ({
                                fieldName: field.name,
                                displayName: field.name,
                                replaceValue: '',
                                search: false,
                                piField: false,
                            }));
                        }

                        if (data.parentTableName) {
                            const parentTable = db.tables.find(t => t.tableName === data.parentTableName);

                            if (parentTable) {
                                this.parentFields = parentTable.fields.map(field => ({
                                    fieldName: field.name,
                                    displayName: field.name,
                                    replaceValue: '',
                                    search: false,
                                    piField: false,
                                }));
                            }
                        }
                    }
                },
                error: (err) => {
                    this.toastService.show('Error loading mapping: ' + (err.error?.message || 'Unknown error'), 'error');
                }
            }
        );
    }

    cancel() {
        this.formValues = {
            displayName: '',
            dbName: '',
            tableName: '',
            parentTableName: '',
            parentIdColumn: '',
            identifierColumn: '',
        };

        this.schemaFields = [];
        this.tables = [];
        this.fields = [];
    }
}